import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import AppBar from "@mui/material/AppBar";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { useNavigate } from "react-router-dom";
import { BigTile, TextComponent } from "../Common/Tile";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { makeStyles } from "tss-react/mui";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";

import WorkspaceClass from "../../service/workspace";
import { ButtonUI } from "../Common";
import AddForm from "./AddForm";
import NoSnowflakeAssociated from "../Common/NoSnowflakeAssociated";
import CommonLayout from "../Common/Layout";
import { syncNotifications } from "../../store/actions";
import { convertTimezone } from "../../util/common";

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 8,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
    paddingLeft: 30,
    paddingRight: 30,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
  },
  NoData: {
    display: "flex",
    justifyContent: "center",
  },
  subContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    borderTop: 0,
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
  },
  loadingAnimation: {
    animation: "spin 2s ease-in-out infinite -1.5s",
  },
}));

const MyWorkspace = () => {
  const [loading, setLoading] = useState(true);
  const [openAddForm, setOpenAddForm] = useState(false);
  const [ownedWorkspaceList, setOwnedWorkspaceList] = useState([]);
  const [sharedWorkspaceList, setSharedWorkspaceList] = useState([]);
  const [hasSnowflakeAssociated, setHasSnowflakeAssociated] = useState(true);

  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const { classes } = useStyles();
  const navigate = useNavigate();

  const { isMenuAllowed, isSysAdmin, isBusinessAdmin, isDataManager } = useMemo(() => {
    let [isMenuAllowed, isSysAdmin, isBusinessAdmin, isDataManager] = [false, false, false, false];
    for (const role of user.currentUser.Roles) {
      if (["SysAdmin", "BusinessAdmin", "DataManager"].includes(role)) isMenuAllowed = true;

      if (role === "BusinessAdmin") isBusinessAdmin = true;
      else if (role === "SysAdmin") isSysAdmin = true;
      else if (role === "DataManager") isDataManager = true;
    }
    return { isMenuAllowed, isSysAdmin, isBusinessAdmin, isDataManager };
  }, [user.currentUser.Roles]);

  const loadWorkspaceList = async () => {
    setLoading(true);
    await getWorkspaceList();
    setLoading(false);
  };

  const getWorkspaceList = async () => {
    const result = await new WorkspaceClass().getData();

    let syncWorkspaceIdList = [];
    if (result.success) {
      const responseData = result.data;

      if (responseData.owned) {
        setOwnedWorkspaceList(responseData.owned);
        responseData.owned.forEach((ws) => {
          if (ws.CreatedDate === null) syncWorkspaceIdList.push(ws.Workspace_id);
        });
      }

      if (responseData.shared) {
        setSharedWorkspaceList(responseData.shared);
        responseData.shared.forEach((ws) => {
          if (ws.CreatedDate === null) syncWorkspaceIdList.push(ws.Workspace_id);
        });
      }
    } else if (result.errorType === "credentials") setHasSnowflakeAssociated(false);

    if (syncWorkspaceIdList.length > 0) {
      setTimeout(async () => {
        await getWorkspaceList();
        dispatch(syncNotifications({ sync: true }));
      }, 30000);
    }
  };

  const handleTileClick = (Workspace_id) => {
    navigate("/my-workspaces/" + Workspace_id);
  };

  useEffect(() => {
    loadWorkspaceList();
  }, []);

  const breadcrumbArr = [{ link: "#", displayName: "Workspaces", active: true }];

  return (
    <CommonLayout title="Workspaces" breadcrumbArr={breadcrumbArr}>
      <Box
        sx={{
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>

        {isMenuAllowed ? (
          <AppBar
            sx={{
              position: "relative",
              boxShadow: 0,
              backgroundColor: "#e8edf1",
            }}
          >
            <Toolbar className={classes.Header}>
              {user.currentUser.Roles.includes("DataManager") && (
                <ButtonUI
                  data-id="wsAddWorkspace"
                  endIcon={<AddIcon />}
                  variant="contained"
                  color="info"
                  style={{ width: "150px", marginRight: "10px" }}
                  onClick={(e) => setOpenAddForm(true)}
                  disabled={!hasSnowflakeAssociated}
                >
                  Add Workspace
                </ButtonUI>
              )}
              {(isBusinessAdmin || (isSysAdmin && isDataManager)) && (
                <ButtonUI
                  data-id="wsAddWorkspace"
                  endIcon={<AutoGraphIcon />}
                  variant="contained"
                  color="info"
                  style={{ width: "175px", marginRight: "10px" }}
                  onClick={(e) => navigate("/manage-workspaces")}
                  disabled={!hasSnowflakeAssociated}
                >
                  Manage Workspaces
                </ButtonUI>
              )}
            </Toolbar>
          </AppBar>
        ) : (
          <></>
        )}

        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingBottom: 15 }}>
          {hasSnowflakeAssociated ? (
            <Box
              // maxWidth={!loading && !ownedWorkspaceList.length && !sharedWorkspaceList.length ? "false" : "xl"}
              className={classes.subContainer}
              sx={{ width: "100%" }}
            >
              <Grid container direction="column" justifyContent="flex-end" alignItems="left">
                <Grid className={classes.PanelContainer}>
                  {!loading && ownedWorkspaceList.length ? (
                    <>
                      <Typography className={classes.HeaderTitle} gutterBottom component="div">
                        Owned by Me
                      </Typography>
                      <Grid container spacing={5} direction="row" sx={{ mb: 3 }}>
                        {ownedWorkspaceList.map((row, key) => {
                          return (
                            <Grid
                              key={key}
                              size={{
                                sm: 6,
                                xs: 12,
                                md: 4,
                                lg: 4,
                                xl: 3
                              }}>
                              <BigTile
                                configData={{
                                  isProcessing: row.CreatedDate === null,
                                  process: (
                                    <>
                                      <AutorenewIcon
                                        sx={{ fontSize: 25 }}
                                        color="info"
                                        className={classes.loadingAnimation}
                                      />
                                      Creating Workspace
                                    </>
                                  ),
                                  dataId: "owned" + row.Name,
                                  borderLeft: `10px solid ${row.Type === 2 ? "#f6ca9f" : "#bddbaf"}`,
                                  cursor: "pointer",
                                  label: row.Name,
                                  link: row.Workspace_id,
                                  location: row.Owner ? (
                                    <TextComponent label="Owner" value={row.Owner} paragraph={true} />
                                  ) : (
                                    ""
                                  ),
                                  totalFields: row.CreatedDate ? (
                                    <TextComponent
                                      label="Created on"
                                      value={convertTimezone(row.CreatedDate || null) || ""}
                                    />
                                  ) : (
                                    ""
                                  ),
                                  createdBy: row.Creator ? (
                                    <TextComponent label="Created by" value={row.Creator} />
                                  ) : (
                                    ""
                                  ),
                                  ...(row.IsDataVaultEnabled
                                    ? {
                                        textTransform: "uppercase",
                                        chips: [
                                          {
                                            text: "DV 2.0",
                                            chipTextColor: "#14769A",
                                            chipBGColor: "#C7DDF2",
                                          },
                                        ],
                                      }
                                    : {}),
                                  clickEvent: handleTileClick,
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </>
                  ) : (
                    ""
                  )}
                  {!loading && sharedWorkspaceList.length ? (
                    <>
                      <Typography className={classes.HeaderTitle} gutterBottom component="div">
                        Shared with Me
                      </Typography>
                      <Grid container spacing={5} direction="row">
                        {sharedWorkspaceList.map((row, key) => {
                          return (
                            <Grid
                              key={key}
                              size={{
                                sm: 6,
                                xs: 12,
                                md: 4,
                                lg: 4,
                                xl: 3
                              }}>
                              <BigTile
                                configData={{
                                  isProcessing: row.CreatedDate === null,
                                  process: (
                                    <>
                                      <AutorenewIcon
                                        sx={{ fontSize: 25 }}
                                        color="info"
                                        className={classes.loadingAnimation}
                                      />
                                      Creating Workspace
                                    </>
                                  ),
                                  dataId: "shared" + row.Name,
                                  borderLeft: "10px solid #CDE4F0",
                                  cursor: "pointer",
                                  label: row.Name,
                                  link: row.Workspace_id,
                                  location: row.Owner ? (
                                    <TextComponent label="Owner" value={row.Owner} paragraph={true} />
                                  ) : (
                                    ""
                                  ),
                                  totalFields: row.CreatedDate ? (
                                    <TextComponent
                                      label="Created on"
                                      value={convertTimezone(row.CreatedDate || null) || ""}
                                    />
                                  ) : (
                                    ""
                                  ),
                                  createdBy: row.Creator ? (
                                    <TextComponent label="Created by" value={row.Creator} />
                                  ) : (
                                    ""
                                  ),
                                  ...(row.IsDataVaultEnabled
                                    ? {
                                        textTransform: "uppercase",
                                        chips: [
                                          {
                                            text: "DV 2.0",
                                            chipTextColor: "#14769A",
                                            chipBGColor: "#C7DDF2",
                                          },
                                        ],
                                      }
                                    : {}),
                                  clickEvent: handleTileClick,
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </>
                  ) : (
                    ""
                  )}

                  {!loading && !ownedWorkspaceList.length && !sharedWorkspaceList.length ? (
                    <Grid sx={{ mb: 2, mt: 5 }} className={classes.NoData} size={12}>
                      <Typography variant="h6" color={"error"} style={{ color: "#000000d9" }} textTransform="none">
                        You do not have any workspace owned or shared.
                      </Typography>
                    </Grid>
                  ) : (
                    ""
                  )}

                  {openAddForm && (
                    <AddForm open={openAddForm} setOpen={setOpenAddForm} loadWorkspaceList={loadWorkspaceList} />
                  )}
                </Grid>
              </Grid>
            </Box>
          ) : (
            <NoSnowflakeAssociated />
          )}
        </Box>
      </Box>
    </CommonLayout>
  );
};

export default MyWorkspace;
